import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteDataCategory,
    getDataCategories,
    updateDataCategoryStatus,
} from '../store/dataCategories/actions';
import { capitalizeFirst } from '../commonUtils/string.helper';
import { hasPermission } from '../commonUtils/permission.helper';
import constants from '../commonUtils/constants';
import { Switch } from "../components/ui/switch";
import IconButton from '../components/IconButton';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

const DataCategoriesContext = createContext();

export const useDataCategories = () => useContext(DataCategoriesContext);

export const DataCategoriesProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { filters, DataCategoriesList, loading, errors } = useSelector((state) => state.DataCategories);

    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");
    const [updateData, setUpdateData] = useState({ _id: "", title: "" });

    const debouncedGetDataCategories = useMemo(() => debounce((params) => {
        dispatch(getDataCategories(params));
    }, 300), [dispatch]);

    useEffect(() => {
        debouncedGetDataCategories({
            currentPage: 1,
            perPage: DataCategoriesList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            ...filters,
        });
    }, [filters, DataCategoriesList?.pagination?.perPage, debouncedGetDataCategories]);

    useEffect(() => {
        handleCloseModal('update');
        handleCloseModal('delete');
    }, [DataCategoriesList]);

    const dataCategories = useMemo(() => DataCategoriesList?.data?.map(
        ({ _id, key, title, status }, index) => ({
            id: index + 1,
            _id,
            key,
            title,
            status: capitalizeFirst(status),
        })
    ), [DataCategoriesList]);

    const pagination = useMemo(() => DataCategoriesList?.pagination, [DataCategoriesList]);
            
    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetDataCategories({ currentPage, perPage, ...filters });
    }, [debouncedGetDataCategories, filters]);

    const editDataCategory = useCallback((data) => setUpdateData(data), []);
    const handleToggleStatus = useCallback((id) => dispatch(updateDataCategoryStatus(id)), [dispatch]);
    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const columnHelper = useMemo(() => createColumnHelper(), []);
    const columns = useMemo(() => [
        columnHelper.accessor('id', { header: '#', cell: info => info.getValue() }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('title', { header: 'Title', cell: info => info.getValue() }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: info => (
                <Switch
                    checked={info.getValue() === 'Active'}
                    onCheckedChange={() => handleToggleStatus(info.row.original._id)}
                    disabled={!hasPermission('dataCategories.update')}
                />
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    {hasPermission('dataCategories.update') && (
                        <IconButton variant="action_default" size="icon" Icon={PencilSquareIcon} onClick={() => editDataCategory(info.row.original)} />
                    )}
                    {hasPermission('dataCategories.create_delete') && (
                        <IconButton variant="action_default" size="icon" Icon={TrashIcon} onClick={() => askDeleteConfirmation(info.row.original._id)} />
                    )}
                </>
            ),
        }),
    ], [columnHelper, handleToggleStatus, editDataCategory, askDeleteConfirmation]);

    const table = useReactTable({
        data: dataCategories,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageIndex: DataCategoriesList?.pagination?.currentPage - 1,
                pageSize: DataCategoriesList?.pagination?.perPage,
            },
        },
        manualPagination: true,
        pageCount: DataCategoriesList?.pagination?.totalPages,
    });

    const handleCloseModal = (action) => {
        if(action === "update"){
            setUpdateData({id:"", title:""})
            setOpenUpdateModal(false);
        } else if(action === "delete") {
            setToDeleteId("");
            setShowDeleteConfirmationDialog(false);
        }
    }
    const handleDelete = () => {
        dispatch(deleteDataCategory(toDeleteId));
        handleCloseModal('delete');
    };

    return (
        <DataCategoriesContext.Provider value={{
            dataCategories,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editDataCategory,
            openUpdateModal,
            setOpenUpdateModal,
            updateData,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
        }}>
            {children}
        </DataCategoriesContext.Provider>
    );
};
